/**
 * Dashboard Page Help Content
 *
 * Includes both page-level and section-level help content.
 * Section-level content can be accessed via contentId like "dashboard:quick-links"
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createRelatedLink,
} from '../utils';

// ============================================================================
// PAGE-LEVEL HELP
// ============================================================================

export const dashboardHelp: HelpContent = createHelpContent({
  id: 'dashboard',
  title: 'Dashboard',
  description: 'Your personalized workspace overview',
  lastUpdated: '2026-02-04',

  tips: [
    createTip(
      'tip-keyboard',
      'pro-tip',
      'Quick Help',
      'Press "?" anywhere to open contextual help. Look for the question mark icons next to sections for specific guidance.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      '<p>The Dashboard is your central hub for staying updated on company activities and managing your daily work. It provides a quick snapshot of your tasks, upcoming deadlines, recent news, and team activities.</p>'
    ),
    createSection(
      'widgets',
      'Dashboard Widgets',
      `<p>Your dashboard displays several widgets:</p>
      <ul>
        <li><strong>Quick Stats</strong> - Key metrics at a glance including hours logged, allocation percentage, and active projects</li>
        <li><strong>Quick Links</strong> - Fast access to common actions and pages</li>
        <li><strong>Latest News</strong> - Recent company announcements</li>
        <li><strong>My Projects</strong> - Projects you're assigned to</li>
        <li><strong>My Allocation</strong> - Your current workload distribution</li>
        <li><strong>Training</strong> - Your learning progress</li>
      </ul>`
    ),
    createSection(
      'stats',
      'Understanding Your Stats',
      `<p>The stats bar at the top shows:</p>
      <ul>
        <li><strong>Hours Logged</strong> - Total hours you've recorded this week in your timesheet</li>
        <li><strong>Allocated %</strong> - Percentage of your capacity assigned to projects</li>
        <li><strong>Active Projects</strong> - Number of projects you're currently working on</li>
        <li><strong>Milestones</strong> - Upcoming project milestones you're involved in</li>
      </ul>`
    ),
    createSection(
      'navigation',
      'Quick Navigation',
      '<p>Use the sidebar to navigate to different sections of the application. The search bar at the top allows you to quickly find content, colleagues, projects, and more across the entire platform.</p>'
    ),
  ],

  faqs: [
    createFAQ(
      'faq-customize',
      'How do I customize my dashboard?',
      'Dashboard customization features are coming soon. Currently, the dashboard displays a standard set of widgets optimized for productivity. Manager-specific widgets appear automatically based on your role.',
      ['customize', 'widgets', 'settings', 'personalize']
    ),
    createFAQ(
      'faq-tasks',
      'Why don\'t I see all my tasks on the dashboard?',
      'The dashboard shows your most recent and high-priority tasks. To see all your tasks, click on "My Tasks" in the sidebar or use the "View All" link in the tasks widget.',
      ['tasks', 'visibility', 'widget', 'all tasks']
    ),
    createFAQ(
      'faq-refresh',
      'How often is the dashboard data refreshed?',
      'Dashboard data is refreshed automatically when you load the page. For real-time updates, simply refresh your browser or navigate away and back to the dashboard.',
      ['refresh', 'data', 'updates', 'real-time']
    ),
    createFAQ(
      'faq-mobile',
      'Can I access the dashboard on mobile devices?',
      'Yes, the dashboard is fully responsive and works on mobile devices. The layout adjusts automatically to provide the best experience on smaller screens.',
      ['mobile', 'responsive', 'phone', 'tablet']
    ),
    createFAQ(
      'faq-manager',
      'Why do I see different widgets than my colleagues?',
      'Some widgets are role-specific. Managers and team heads see additional widgets like Team Capacity, Project Health, and Milestones that help with team oversight.',
      ['manager', 'widgets', 'role', 'different']
    ),
  ],

  relatedLinks: [
    createRelatedLink('My Tasks', '/my-tasks', { description: 'View and manage all your assigned tasks' }),
    createRelatedLink('News & Announcements', '/news', { description: 'Read all company news and updates' }),
    createRelatedLink('Training', '/training', { description: 'Access your learning and development resources' }),
    createRelatedLink('Timesheet', '/timesheet', { description: 'Log your work hours' }),
  ],

  keywords: ['home', 'overview', 'start', 'main', 'widgets', 'summary', 'stats'],
});

// ============================================================================
// SECTION-LEVEL HELP
// ============================================================================

/**
 * Quick Links section help
 * Use with: <SectionHelp contentId="dashboard:quick-links">
 */
export const dashboardQuickLinksHelp: HelpContent = createHelpContent({
  id: 'dashboard:quick-links',
  title: 'Quick Links',
  description: 'Fast access to common actions',
  lastUpdated: '2026-02-04',
  parentId: 'dashboard',

  sections: [
    createSection(
      'overview',
      'What are Quick Links?',
      `<p>Quick Links provide one-click access to the most commonly used features and pages in the system. They help you navigate faster without going through menus.</p>
      <p>Available quick links include:</p>
      <ul>
        <li><strong>Log Time</strong> - Jump directly to your timesheet</li>
        <li><strong>Submit Leave</strong> - Request time off</li>
        <li><strong>New Task</strong> - Create a task quickly</li>
        <li><strong>Directory</strong> - Find colleagues</li>
      </ul>`
    ),
  ],

  faqs: [
    createFAQ(
      'faq-customize-links',
      'Can I customize which quick links appear?',
      'Quick links are currently standard for all users. Customization features are planned for a future update.',
      ['customize', 'personalize', 'change']
    ),
  ],

  relatedLinks: [],
  keywords: ['quick', 'links', 'shortcuts', 'navigation'],
});

/**
 * My Allocation section help
 * Use with: <SectionHelp contentId="dashboard:allocation">
 */
export const dashboardAllocationHelp: HelpContent = createHelpContent({
  id: 'dashboard:allocation',
  title: 'My Allocation',
  description: 'Understanding your workload distribution',
  lastUpdated: '2026-02-04',
  parentId: 'dashboard',

  sections: [
    createSection(
      'overview',
      'What is Allocation?',
      `<p>Allocation shows how your work capacity is distributed across projects. It helps you and your manager understand your current workload.</p>
      <ul>
        <li><strong>Allocation %</strong> - Percentage of your weekly capacity assigned to projects</li>
        <li><strong>Under 100%</strong> - You have available capacity for more work</li>
        <li><strong>100%</strong> - You're fully allocated</li>
        <li><strong>Over 100%</strong> - You may be over-committed (shown in red)</li>
      </ul>`
    ),
    createSection(
      'projects',
      'Project Breakdown',
      `<p>The widget shows which projects you're allocated to and how many hours per week. This is set by your project manager or team lead in the resource management system.</p>`
    ),
  ],

  faqs: [
    createFAQ(
      'faq-change-allocation',
      'How do I change my allocation?',
      'Allocations are managed by project managers and team leads. If you think your allocation is incorrect, speak with your manager or the project manager.',
      ['change', 'update', 'modify']
    ),
    createFAQ(
      'faq-over-allocated',
      'What if I\'m over-allocated?',
      'If your allocation exceeds 100%, it may indicate competing priorities. Discuss with your manager to prioritize or redistribute work.',
      ['over', 'capacity', 'too much']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Resources', '/resources', { description: 'View resource allocation details' }),
  ],
  keywords: ['allocation', 'capacity', 'workload', 'hours', 'projects'],
});

/**
 * Effort Summary section help
 */
export const dashboardEffortHelp: HelpContent = createHelpContent({
  id: 'dashboard:effort',
  title: 'Effort Summary',
  description: 'Your time tracking overview',
  lastUpdated: '2026-02-04',
  parentId: 'dashboard',

  sections: [
    createSection(
      'overview',
      'Understanding Effort Summary',
      `<p>The Effort Summary shows your time logging statistics:</p>
      <ul>
        <li><strong>This Week Logged</strong> - Hours you've recorded in your timesheet this week</li>
        <li><strong>Expected</strong> - Hours you're expected to log based on your work schedule</li>
        <li><strong>Last Week</strong> - Your total from the previous week for comparison</li>
        <li><strong>Variance</strong> - Difference between expected and actual hours</li>
      </ul>`
    ),
  ],

  faqs: [
    createFAQ(
      'faq-log-time',
      'How do I log my time?',
      'Click on "Timesheet" in the sidebar or use the Quick Link to access your timesheet. Select the project and task, enter hours, and save.',
      ['log', 'timesheet', 'hours', 'record']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Timesheet', '/timesheet', { description: 'Log your work hours' }),
  ],
  keywords: ['effort', 'hours', 'time', 'logging', 'timesheet'],
});

/**
 * Latest News section help
 */
export const dashboardNewsHelp: HelpContent = createHelpContent({
  id: 'dashboard:news',
  title: 'Latest News',
  description: 'Recent company announcements',
  lastUpdated: '2026-02-04',
  parentId: 'dashboard',

  sections: [
    createSection(
      'overview',
      'Latest News Widget',
      `<p>This widget shows the most recent company announcements and news. Click on any item to read the full article.</p>
      <p>To see all news and announcements, click "View All" or navigate to the News page from the sidebar.</p>`
    ),
  ],

  faqs: [],

  relatedLinks: [
    createRelatedLink('All News', '/news', { description: 'View all company news' }),
  ],
  keywords: ['news', 'announcements', 'updates'],
});
